<script>
export default {
  name: "DidoDownloadButtonMillesime",
};
</script>

<script setup>
import { ref, watch, computed } from "vue";
import api from "../../../api";

import parquetPng from "../../../assets/parquet.png";

const parquetEnabled = ref(false);
api.withParquet(props.apiUrl).then((result) => (parquetEnabled.value = result));

// define Props
const props = defineProps({
  apiUrl: { type: String, required: true },
  millesimeObject: { type: Object, required: true },
  selectable: { type: Boolean, required: false, default: false },
  selectedLinks: { type: Array, required: false, default: () => [] },
});

// define emits
const emit = defineEmits(["update-selected-links"]);

// define Reactive Data
const csvSelected = ref(false);
const xlsxSelected = ref(false);
const jsonSelected = ref(false);
const parquetSelected = ref(false);
const spatialsSelected = ref({
  csvSelected: false,
  xlsxSelected: false,
  jsonSelected: false,
  parquetSelected: false,
  spatialsSelected: (props.millesimeObject.geoFields || []).reduce(
    (acc, cur) => {
      acc[cur] = { geojsonSelected: false };
      return acc;
    },
    {},
  ),
});

// define computed datas
const buttonTooltip = computed(() => {
  return props.millesimeObject
    ? `Télécharger le millésime ${millesime.value} du fichier de données '${title.value}' au format csv`
    : null;
});
const title = computed(() =>
  props.millesimeObject ? props.millesimeObject.title : null,
);
const rid = computed(() =>
  props.millesimeObject ? props.millesimeObject.rid : null,
);
const millesime = computed(() =>
  props.millesimeObject ? props.millesimeObject.millesime : null,
);
const geoFields = computed(() =>
  props.millesimeObject ? props.millesimeObject.geoFields || [] : null,
);
const baseLink = computed(() =>
  props.millesimeObject ? api.urlDatafileBase(props.apiUrl, rid.value) : null,
);
const links = computed(() =>
  props.millesimeObject
    ? {
        csv: {
          link: `${baseLink.value}/csv?millesime=${millesime.value}&withColumnName=true&withColumnDescription=true&withColumnUnit=false`,
          label: "Téléchargement au format csv",
          title: `Télécharger le millésime ${millesime.value} du fichier de données '${title.value}' au format csv`,
        },
        xlsx: {
          link: `${baseLink.value}/xlsx?millesime=${millesime.value}&withColumnName=true&withColumnDescription=true&withColumnUnit=false`,
          label: "Téléchargement au format xlsx",
          title: `Télécharger le millésime ${millesime.value} du fichier de données '${title.value}' au format xlsx`,
        },
        json: {
          link: `${baseLink.value}/json?millesime=${millesime.value}`,
          label: "Téléchargement au format json",
          title: `Télécharger le millésime ${millesime.value} du fichier de données '${title.value}' au format json`,
        },
        parquet: {
          link: `${baseLink.value}/parquet?millesime=${millesime.value}`,
          label: "Téléchargement au format parquet",
          title: `Télécharger le millésime ${millesime.value} du fichier de données '${title.value}' au format parquet`,
        },
        spatials: geoFields.value.map((geoField) => ({
          geoField,
          geojson: {
            link: `${baseLink.value}/spatial/geojson?millesime=${millesime.value}&geoField=${geoField}`,
            label: `Téléchargement au format geojson (ref ${geoField})`,
            title: `Télécharger le millésime ${millesime.value} du fichier de données '${title.value}' au format geojson avec ${geoField} comme champ géographique de référence`,
          },
        })),
        api: {
          link: `${baseLink.value}/apidoc.html?millesime=${millesime.value}`,
          label: "Voir l'apidoc",
          title: `Accéder à la documentation de l'api du millésime ${millesime.value} du fichier de données '${title.value}'`,
        },
      }
    : null,
);

// define watchers
watch(
  () => [props.millesimeObject, props.selectedLinks],
  () => checkSelectedLinks(),
);

// methods
const checkSelectedLinks = () => {
  if (props.millesimeObject) {
    csvSelected.value = !!props.selectedLinks.find(
      (l) => l === links.value.csv.link,
    );
    xlsxSelected.value = !!props.selectedLinks.find(
      (l) => l === links.value.xlsx.link,
    );
    jsonSelected.value = !!props.selectedLinks.find(
      (l) => l === links.value.json.link,
    );
    parquetSelected.value = !!props.selectedLinks.find(
      (l) => l === links.value.parquet.link,
    );
    spatialsSelected.value = (props.millesimeObject.geoFields || []).reduce(
      (acc, cur) => {
        acc[cur] = {
          geojsonSelected: !!props.selectedLinks.find(
            (l) =>
              l ===
              links.value.spatials.find(({ geoField }) => geoField === cur)
                .geojson.link,
          ),
        };
        return acc;
      },
      {},
    );
  }
};
const toggleLink = (selected, link) => {
  if (selected) emit("update-selected-links", { action: "add", link });
  if (!selected) emit("update-selected-links", { action: "remove", link });
};

// Init
checkSelectedLinks();
</script>

<template>
  <div v-if="props.millesimeObject">
    <QBtnDropdown
      :href="links.csv.link"
      target="_blank"
      split
      color="secondary"
      no-caps
      dense
      icon="mdi-download"
      label="télécharger le csv"
      size="sm"
      unelevated
      :title="buttonTooltip"
      :content-class="['no-border-radius', 'no-box-shadow', 'border-secondary']"
      class="no-border-radius"
    >
      <!-- List of download formats -->
      <QList dense>
        <!-- Csv Link -->
        <QItem
          v-close-popup
          :href="links.csv.link"
          target="_blank"
          dense
          clickable
          :title="links.csv.title"
        >
          <QItemSection>
            <QItemLabel>
              <QIcon name="mdi-file-delimited" color="secondary" left />
              <span class="text-black text-caption">{{ links.csv.label }}</span>
            </QItemLabel>
          </QItemSection>
          <QItemSection v-if="props.selectable" side>
            <QCheckbox
              v-model="csvSelected"
              dense
              keep-color
              size="xs"
              color="secondary"
              @update:model-value="toggleLink(csvSelected, links.csv.link)"
            />
          </QItemSection>
        </QItem>
        <!-- Xlsx Link -->
        <QItem
          v-close-popup
          :href="links.xlsx.link"
          target="_blank"
          dense
          clickable
          :title="links.xlsx.title"
        >
          <QItemSection>
            <QItemLabel>
              <QIcon name="mdi-microsoft-excel" color="secondary" left />
              <span class="text-black text-caption">{{
                links.xlsx.label
              }}</span>
            </QItemLabel>
          </QItemSection>
          <QItemSection v-if="props.selectable" side>
            <QCheckbox
              v-model="xlsxSelected"
              dense
              keep-color
              size="xs"
              color="secondary"
              @update:model-value="toggleLink(xlsxSelected, links.xlsx.link)"
            />
          </QItemSection>
        </QItem>
        <!-- Json Link -->
        <QItem
          v-close-popup
          :href="links.json.link"
          target="_blank"
          dense
          clickable
          :title="links.json.title"
        >
          <QItemSection>
            <QItemLabel>
              <QIcon name="mdi-code-json" color="secondary" left />
              <span class="text-black text-caption">{{
                links.json.label
              }}</span>
            </QItemLabel>
          </QItemSection>
          <QItemSection v-if="props.selectable" side>
            <QCheckbox
              v-model="jsonSelected"
              dense
              keep-color
              size="xs"
              color="secondary"
              @update:model-value="toggleLink(jsonSelected, links.json.link)"
            />
          </QItemSection>
        </QItem>
        <!-- Parquet Link -->
        <QItem
          v-if="parquetEnabled"
          v-close-popup
          :href="links.parquet.link"
          target="_blank"
          dense
          clickable
          :title="links.parquet.title"
        >
          <QItemSection>
            <QItemLabel>
              <QIcon color="secondary" left>
                <img :src="parquetPng" />
              </QIcon>
              <span class="text-black text-caption">{{
                links.parquet.label
              }}</span>
            </QItemLabel>
          </QItemSection>
          <QItemSection v-if="props.selectable" side>
            <QCheckbox
              v-model="parquetSelected"
              dense
              keep-color
              size="xs"
              color="secondary"
              @update:model-value="
                toggleLink(parquetSelected, links.parquet.link)
              "
            />
          </QItemSection>
        </QItem>
        <!-- Spatial links -->
        <template v-if="links.spatials.length">
          <!-- One Block for each GeoField -->
          <template
            v-for="(geofieldSpecs, index) in links.spatials"
            :key="`geojson-${index}`"
          >
            <QSeparator color="secondary" />
            <!-- GeoJson Link -->
            <QItem
              v-close-popup
              :href="geofieldSpecs.geojson.link"
              target="_blank"
              dense
              clickable
              :title="geofieldSpecs.geojson.title"
            >
              <QItemSection>
                <QItemLabel>
                  <QIcon name="mdi-code-json" color="secondary" left />
                  <span class="text-black text-caption">{{
                    geofieldSpecs.geojson.label
                  }}</span>
                </QItemLabel>
              </QItemSection>
              <QItemSection v-if="props.selectable" side>
                <QCheckbox
                  v-model="
                    spatialsSelected[geofieldSpecs.geoField].geojsonSelected
                  "
                  dense
                  keep-color
                  size="xs"
                  color="secondary"
                  @update:model-value="
                    toggleLink(
                      spatialsSelected[geofieldSpecs.geoField].geojsonSelected,
                      geofieldSpecs.geojson.link,
                    )
                  "
                />
              </QItemSection>
            </QItem>
          </template>
        </template>
        <!-- Apidoc -->
        <QSeparator color="secondary" />
        <QItem
          v-close-popup
          :href="links.api.link"
          target="_blank"
          dense
          clickable
          :title="links.api.title"
        >
          <QItemSection>
            <QItemLabel>
              <QIcon name="mdi-cogs" color="secondary" class="on-left" />
              <span class="text-black text-caption">
                {{ links.api.label }}
              </span>
            </QItemLabel>
          </QItemSection>
        </QItem>
      </QList>
    </QBtnDropdown>
  </div>
</template>
